/**
 * Every kind of in-app entity an alert, opportunity or agent message can link
 * to, with what its id is. Shared by the agent prompts, the "Connected to"
 * input schema, backend link helpers and the dashboard.
 */
const ENTITY_KINDS = {
    page: 'dynamicPageId',
    variant: '"{dynamicPageId}/{variantId}"',
    experiment: '"{dynamicPageId}/{experimentId}"',
    quiz: 'quizId',
    embeddedForm: 'quizId of an embedded form',
    form: 'formId',
    adsAccount: 'the ads account id',
    integration: 'integrationId, also for webhooks',
    connector: 'the connector installation id',
    skill: 'skillId',
    knowledge: 'any id, it opens the Brain knowledge screen',
    memory: 'any id, it opens the Brain memory screen',
    externalLink: 'the full http(s) URL, it opens in a new tab',
    settings: 'one of these settings routes exactly, no others: "website", ' +
        '"website?tab={links|content|domains|brand-styles|search-and-social|' +
        'tracking|custom-code}", "ai-settings", ' +
        '"ai-settings?tab={brand_voice|audience|content_rules}", "cms", ' +
        '"embedded-forms", "integrations/apps", "integrations/webhooks", ' +
        '"integrations/ad-accounts", "integrations/api-keys", "billing", ' +
        '"billing?tab={plan|usage}", "organization", ' +
        '"organization?tab={profile|notifications|members}"',
};
const ENTITY_KIND_NAMES = Object.keys(ENTITY_KINDS);
/** Markdown links in this scheme render as clickable entity chips. */
const ENTITY_LINK_SCHEME = 'sf-entity:';
/** "kind (id)" for every kind, for agent prompts and tool descriptions. */
const describeEntityKinds = () => Object.entries(ENTITY_KINDS)
    .map(([kind, id]) => `${kind} (${id})`)
    .join(', ');
/** A markdown chip linking to an entity: `[name](sf-entity:{kind}/{id})`. */
const entityLink = (kind, id, name) => `[${name.replace(/[\\[\]]/g, '\\$&')}](${ENTITY_LINK_SCHEME}${kind}/${id})`;
/** The entity an `sf-entity:` href points at, or null for any other href. */
function parseEntityLink(href, name) {
    if (!href?.startsWith(ENTITY_LINK_SCHEME))
        return null;
    const [kind, ...id] = href.slice(ENTITY_LINK_SCHEME.length).split('/');
    return { kind, label: id.join('/'), name };
}
const pageOf = (label) => encodeURIComponent(label.split('/')[0]);
/** Workspace-relative paths of the editor routes (`/workspace/{slug}` + path). */
const pageEditorPath = (dynamicPageId, websiteId) => `/pages/${encodeURIComponent(dynamicPageId)}/editor/${encodeURIComponent(websiteId)}`;
const quizEditorPath = (quizId, { formId, embedded } = {}) => {
    const listPath = embedded ? '/settings/embedded-forms' : '/quiz-funnels';
    const search = formId ? `?formId=${encodeURIComponent(formId)}` : '';
    return `${listPath}/${encodeURIComponent(quizId)}/editor${search}`;
};
const globalSectionEditorPath = (globalSectionId) => `/global-sections/${encodeURIComponent(globalSectionId)}`;
const ENTITY_DASHBOARD_PATHS = {
    page: (id) => `pages/${encodeURIComponent(id)}/details`,
    variant: (label) => `pages/${pageOf(label)}/details`,
    experiment: (label) => `pages/${pageOf(label)}/details`,
    quiz: (id) => quizEditorPath(id).slice(1),
    embeddedForm: (id) => quizEditorPath(id, { embedded: true }).slice(1),
    form: () => 'settings/forms',
    adsAccount: () => 'settings/integrations/ad-accounts',
    integration: () => 'settings/integrations/webhooks',
    connector: (id) => `brain/connectors?connector=${encodeURIComponent(id)}`,
    skill: (id) => `brain/skills/${encodeURIComponent(id)}`,
    knowledge: () => 'brain/knowledge',
    memory: () => 'brain/memory',
    settings: (route) => `settings/${route.trim().replace(/^\/?(settings\/)?/, '')}`,
};
const isDashboardEntityKind = (kind) => Object.hasOwn(ENTITY_DASHBOARD_PATHS, kind);
const entityDashboardPath = (kind, label) => ENTITY_DASHBOARD_PATHS[kind](label);

export { ENTITY_KINDS, ENTITY_KIND_NAMES, ENTITY_LINK_SCHEME, describeEntityKinds, entityDashboardPath, entityLink, globalSectionEditorPath, isDashboardEntityKind, pageEditorPath, parseEntityLink, quizEditorPath };
//# sourceMappingURL=connected-entity.esm.js.map
